import { ApplicantDetailsPage } from 'plantItemNew/constants/applicantDetailsPage';
import { PlantDesignApplicantDetailsPage } from 'plantDesign/constants/plantDesignApplicantDetailsPage';
import { PlantDesignDesignerVerifierPage } from 'plantDesign/constants/plantDesignDesignerVerifierPage';
import { PlantDesignDetailsPage } from 'plantDesign/constants/plantDesignDetailsPage';
import { PlantDesignDrawingAndTechDetailsPage } from 'plantDesign/constants/plantDesignDrawingAndTechDetailsPage';
import { findPlantRefByPlantTypeCode } from 'plantDesign/constants/PlantDesignRefData';
import { PlantDesignRegistrationHolderPage } from 'plantDesign/constants/plantDesignRegistrationHolderPage';
import { getWasDesignedBeforeLabel } from 'plantDesign/pages/plantDesignDetails/components/DesignAlterationDetails';
import { formatInternationalAddress, formatSummaryLabels } from '../../shared/utils/FormatHelper';
import { capitalizeFirstLetter, sanitizeHtml } from '../../shared/utils/Helpers';
import { PrivacyCollectionNotice } from 'shared/constants/privacyCollectionNotice';

const formatPlantDesignSummary = (state) => {
  const {
    plantDesignApplicantDetails: applicant,
    plantDesignReferenceData: refData,
    plantDesignHolderDetails: holder,
    plantDesign: design,
    plantDesignDrawingAndTech: drawingsAndTechStandards,
    plantDesignVerifier: designerAndVerifier,
    plantDesignDeclaration: declarations,
  } = state;

  const sections = [];
  sections.push(formatApplicantSummary(applicant));
  sections.push(formatHolderSummary(holder, applicant));
  sections.push(formatDesignSummary(design, refData));
  sections.push(formatDrawingsAndTechStandards(drawingsAndTechStandards));
  sections.push(formatDesignerAndVerifier(designerAndVerifier));

  formatSummaryLabels(sections);

  return { sections, declarations, privacyCollection: PrivacyCollectionNotice };
};

const formatApplicantSummary = (applicantDetails) => {
  const subSections = mapAppicantDetailsSubSections(applicantDetails);
  const section = { title: 'Your details', subSections: subSections };
  return section;
};

const formatHolderSummary = (holderDetails, applicantDetails) => {
  const subSections = mapHolderDetailsSubSections(holderDetails, applicantDetails);
  const section = { title: 'Registration holder details', subSections };
  return section;
};

const formatDesignSummary = (designDetails, refData) => {
  const subSections = mapPlantDesignDetailsSubSections(designDetails, refData);
  const section = { title: 'Plant design details', subSections };
  return section;
};

const formatDrawingsAndTechStandards = (drawingsAndTechStandards) => {
  const subSections = mapDrawingsAndTechnicalStandardsSubSections(drawingsAndTechStandards);
  const section = { title: 'Drawing and technical standards', subSections };
  return section;
};

const formatDesignerAndVerifier = (designerAndVerifierDetails) => {
  const subSections = mapDesignerAndVerifierDetailsSubSections(designerAndVerifierDetails);
  const section = { title: 'Designer and design verifier details', subSections };
  return section;
};

const formatBlankFields = (details) => {
  details.forEach((prop) => (prop.value = prop.value || '-'));
  return details;
};

// Applicant Summary
const mapAppicantDetailsSubSections = (applicantDetails) => {
  const summary = mapApplicantSummary(applicantDetails);
  formatBlankFields(summary);
  const subSection = { title: '', details: summary };
  return [subSection];
};

const mapApplicantSummary = (applicantDetails) => {
  const LABELS = PlantDesignApplicantDetailsPage;

  return [
    { text: LABELS.firstNameLabel, value: applicantDetails['firstName'] },
    { text: LABELS.middleNameLabel, value: applicantDetails['middleName'] },
    { text: LABELS.lastNameLabel, value: applicantDetails['lastName'] },
    { text: LABELS.employerLabel, value: applicantDetails['employer'] },
    { text: LABELS.jobTitleLabel, value: applicantDetails['jobTitle'] },
  ];
};

// Registration Holder Summary
const mapHolderDetailsSubSections = (holderDetails, applicantDetails) => {
  const summary = mapPlantDesignHolderSummary(holderDetails, applicantDetails);

  formatBlankFields(summary);
  const subSection = { title: 'Design registration holder', details: formatBlankFields(summary) };

  return [subSection];
};

const mapPlantDesignHolderSummary = (holderDetails, applicantDetails) => {
  const LABELS = PlantDesignRegistrationHolderPage;

  let summary = [{ text: LABELS.registrationTypeLabel, value: capitalizeFirstLetter(holderDetails.registrationType) }];

  if (holderDetails.registrationType === 'individual') {
    summary = [...summary, ...mapIndividualDetailsSummary(holderDetails, applicantDetails)];
  } else if (holderDetails.registrationType === 'organisation') {
    summary = [...summary, ...mapOrganisationDetailsSummary(holderDetails)];
  }

  return summary;
};

const mapIndividualDetailsSummary = (holderDetails, applicantDetails) => {
  const LABELS = PlantDesignRegistrationHolderPage;

  const details = [
    ...(holderDetails.isRegistrationHolder === 'yes'
      ? getApplicantNameAndEmail(applicantDetails)
      : getHolderNameAndEmail(holderDetails)),
    {
      text: LABELS.residentialAddressLabel,
      value: formatInternationalAddress(holderDetails.residentialAddress),
    },
    mapMailingAddressSummary(holderDetails, 'Same as residential address'),
    {
      text: 'Primary contact number',
      value: holderDetails.contactMethod.primary.formattedNumber,
    },
  ];

  if (holderDetails?.contactMethod?.secondary) {
    details.push({
      text: 'Secondary contact number',
      value: holderDetails.contactMethod.secondary.formattedNumber,
    });
  }

  if (holderDetails?.reasonToRegister) {
    details.push({
      text: PlantDesignRegistrationHolderPage.reasonToRegisterInNSWLabel,
      value: holderDetails.reasonToRegister,
    });
  }

  return details;
};

const getApplicantNameAndEmail = (applicantDetails) => {
  return [
    { text: ApplicantDetailsPage.firstNameLabel, value: applicantDetails.firstName },
    { text: ApplicantDetailsPage.middleNameLabel, value: applicantDetails.middleName },
    { text: ApplicantDetailsPage.lastNameLabel, value: applicantDetails.lastName },
    {
      text: PlantDesignRegistrationHolderPage.holderEmailAddressLabel,
      value: applicantDetails.emailAddress,
    },
  ];
};

const getHolderNameAndEmail = (holderDetails) => {
  const LABELS = PlantDesignRegistrationHolderPage;
  return [
    { text: LABELS.holderFirstNameLabel, value: holderDetails.firstName },
    { text: LABELS.holderMiddleNameLabel, value: holderDetails.middleName },
    { text: LABELS.holderLastNameLabel, value: holderDetails.lastName },
    {
      text: PlantDesignRegistrationHolderPage.holderEmailAddressLabel,
      value: holderDetails.emailAddress,
    },
  ];
};

const mapMailingAddressSummary = (holderDetails, value) => {
  if (holderDetails.isSameAddress) {
    return {
      text: PlantDesignRegistrationHolderPage.mailingAddressLabel,
      value,
    };
  }
  return {
    text: PlantDesignRegistrationHolderPage.mailingAddressLabel,
    value: formatInternationalAddress(holderDetails.mailingAddress),
  };
};

const mapOrganisationDetailsSummary = (holderDetails) => {
  const LABELS = PlantDesignRegistrationHolderPage;

  return [
    {
      text: 'Organisation details',
      value: capitalizeFirstLetter(holderDetails.organisation.organisationName),
      value2: capitalizeFirstLetter(holderDetails.organisation.abn),
    },
    {
      text: 'Registered business name',
      value: capitalizeFirstLetter(holderDetails.organisation.selectedBusinessName),
    },
    { text: 'Organisation email address', value: capitalizeFirstLetter(holderDetails.emailAddress) },
    {
      text: 'Primary contact number',
      value: holderDetails.contactMethod.primary.formattedNumber,
    },
    {
      ...(holderDetails?.contactMethod?.secondary && {
        text: 'Secondary contact number',
        value: holderDetails.contactMethod.secondary.formattedNumber,
      }),
    },
    {
      text: LABELS.registeredBusinessAddressLabel,
      value: formatInternationalAddress(holderDetails.businessAddress),
    },
    mapMailingAddressSummary(holderDetails, 'Same as business address'),
    {
      text: LABELS.reasonToRegisterInNSWLabel,
      value: holderDetails.reasonToRegister,
    },
  ];
};

// Plant Design Summary
const mapPlantDesignDetailsSubSections = (designDetails, refData) => {
  const registrationTypeSummary = mapRegistrationSummary(designDetails);
  const registrationTypeSubSection = {
    title: 'Registration type',
    details: formatBlankFields(registrationTypeSummary),
  };

  const designDetailsSummary = mapPlantDesignSummary(designDetails, refData);
  const designDetailsSubSection = { title: 'Plant design details', details: formatBlankFields(designDetailsSummary) };
  return [registrationTypeSubSection, designDetailsSubSection];
};

const mapPlantDesignSummary = (designDetails, refData) => {
  const plantRef = findPlantRefByPlantTypeCode(designDetails['plantType']);

  return [
    ...mapCommonDesignSummary(designDetails, plantRef),
    ...mapPlantSpecificDesignSummary(designDetails, plantRef, refData.data),
  ];
};

// Do complex mapping here.
const checkForCustomMapping = (prop, value, text, details, designDetails, refData) => {
  const specificDetails = designDetails.plantSpecificDetails;

  const plantTypeCode = designDetails['plantType'];
  // 100 - Boiler
  if (plantTypeCode === '100') {
    if (prop === 'outputUnit') {
      details.push({ text: 'Output', value: specificDetails.outputValue + specificDetails.outputUnit });
      return true;
    }
    if (prop === 'outputValue') return true;
  }
  // 101 - Pressure Vessle
  if (plantTypeCode === '101') {
    if (prop === 'minDesignTemp' || prop === 'maxDesignTemp') {
      details.push({ text, value: sanitizeHtml(value + '&deg;C') });
      return true;
    }
  }
  // 102 - Gas Cylinder
  if (plantTypeCode === '102') {
    if (prop == 'testPressure') {
      details.push({
        text,
        value: value + specificDetails.testPressureUnit,
      });
      return true;
    }
    if (prop == 'testPressureUnit') {
      return true;
    }
  }
  // 116 - Suspended Workbox
  if (plantTypeCode === '116') {
    if (prop === 'isForFirstAidOnly') {
      details.push({ text, value: capitalizeFirstLetter(value) });
      return true;
    }
  }
  // 117 - Prefabricated Scaffolding
  if (plantTypeCode === '117') {
    if (prop === 'isLoadedSimultaneously') {
      details.push({ text, value: capitalizeFirstLetter(value) });
      return true;
    }
  }

  return false;
};

const mapCommonDesignSummary = (designDetails, plantRef) => {
  const LABELS = PlantDesignDetailsPage.common;

  const typeDetails = [{ text: LABELS.plantType.label, value: designDetails['plantTypeDescription'] }];

  if (designDetails.subType) {
    typeDetails.push({
      text: `Type of ${plantRef?.name}`,
      value: designDetails['otherSubType'] || designDetails['subTypeDescription'],
    });
  }
  return [
    ...typeDetails,
    { text: LABELS.manufacturer.label, value: designDetails['manufacturer'] },
    { text: LABELS.modelNameOrNumber.label, value: designDetails['modelNameOrNumber'] },
    { text: 'Year of design', value: designDetails['yearOfDesign'] },
  ];
};

const mapRegistrationSummary = (designDetails) => {
  const LABELS = PlantDesignDetailsPage.designAlteration;

  const registrationSummary = [
    {
      text: PlantDesignDetailsPage.common.registrationTypeLabel,
      value: capitalizeFirstLetter(designDetails.registrationType),
    },
  ];

  if (designDetails.registrationType !== PlantDesignDetailsPage.common.registrationTypes.alteration.value)
    return registrationSummary;

  const { alterationDetails } = designDetails;

  return [
    ...registrationSummary,
    {
      text: getWasDesignedBeforeLabel(designDetails.plantType, designDetails.subType),
      value: capitalizeFirstLetter(alterationDetails.wasDesignedBefore),
    },
    {
      text: LABELS.reasonForAlteration.label,
      value: alterationDetails.reasonForAlteration,
    },
    {
      text: LABELS.registrationNumber.label,
      value: alterationDetails.registrationNumber,
    },
    {
      text: LABELS.registeredLocation.label,
      value: alterationDetails.registeredLocation,
    },
  ];
};

const mapPlantSpecificDesignSummary = (designDetails, plantRef, refData) => {
  let summary = [];
  const specificDetails = designDetails.plantSpecificDetails;

  for (const prop in specificDetails) {
    let suffix = PlantDesignDetailsPage[plantRef.key][prop]?.suffix || '';
    if (suffix === 'year' || suffix === 'years') suffix = ' year(s)';
    const text = PlantDesignDetailsPage[plantRef.key][prop]?.label;
    let value = specificDetails[prop];

    if (checkForCustomMapping(prop, value, text, summary, designDetails, refData)) {
      continue;
    }

    if (value) value = value + ' ' + sanitizeHtml(suffix);

    summary.push({ text, value: value });
  }

  return summary;
};

// Drawings and Technical standards Summary
const mapDrawingsAndTechnicalStandardsSubSections = (drawingsAndTech) => {
  const subSections = [
    { title: 'Drawings', details: mapDrawingsSummary(drawingsAndTech) },
    { title: 'Technical standards', details: mapTechnicalStandardsSummary(drawingsAndTech) },
  ];
  if (drawingsAndTech.engineeringPrinciples.engineeringPrincipleFile?.length) {
    subSections.push({ title: 'Engineering principles', details: mapEngineeringPrinciplesSummary(drawingsAndTech) });
  }

  return subSections;
};

const mapDrawingsSummary = (drawingsAndTech) => {
  const LABELS = PlantDesignDrawingAndTechDetailsPage.drawingList;
  const summary = drawingsAndTech.drawings.flatMap((d) => [
    { text: LABELS.drawingTitleLabel, value: d.title },
    { text: LABELS.drawingNumberLabel, value: d.number },
    { text: LABELS.drawingRevisionNumberLabel, value: d.revisionNumber },
    ...d.uploadedDocument.map((doc) => ({ text: 'Uploaded', value: doc.name })),
  ]);

  formatBlankFields(summary);
  return summary;
};

const mapTechnicalStandardsSummary = (drawingsAndTech) => {
  const LABELS = PlantDesignDrawingAndTechDetailsPage.techStandardList;
  const summary = drawingsAndTech.techStandards.flatMap((ts) => [
    { text: LABELS.techStandardNumberLabel, value: ts.number },
    { text: LABELS.yearOfPublicationLabel, value: ts.yearOfPublication },
  ]);
  summary.push({
    text: PlantDesignDrawingAndTechDetailsPage.engineeringPrinciples.designComplianceLabel,
    value: capitalizeFirstLetter(drawingsAndTech.engineeringPrinciples.complyWithTechStandard),
  });

  formatBlankFields(summary);
  return summary;
};

const mapEngineeringPrinciplesSummary = (drawingsAndTech) => {
  const label = { text: 'Uploaded', value: '' };
  const summary = drawingsAndTech.engineeringPrinciples.engineeringPrincipleFile.map((f) => ({
    text: '',
    value: f.name,
  }));

  return [label, ...summary];
};

// Designer and Verifier Summary
const mapDesignerAndVerifierDetailsSubSections = (plantDesignVerifier) => {
  const { designerDetails, verifierDetails } = plantDesignVerifier;

  let subSections = [];
  // Designer Summary
  let designerSummary = [
    {
      text: PlantDesignDesignerVerifierPage.areYouDesignerLabel,
      value: capitalizeFirstLetter(plantDesignVerifier.isDesigner),
    },
  ];
  // Verifier Summary
  if (plantDesignVerifier.isDesigner !== 'yes') {
    designerSummary = [...designerSummary, ...mapDesignerOrVerifierSummary(designerDetails)];
  }
  subSections.push({ title: 'Designer details', details: formatBlankFields(designerSummary) });
  if (verifierDetails) {
    const verifierSummary = mapDesignerOrVerifierSummary(verifierDetails);
    subSections.push({ title: 'Design verifier details', details: formatBlankFields(verifierSummary) });
  }

  // Designer and Design Verifier Statement
  if (plantDesignVerifier?.uploadedDocument?.length) {
    const statementSummary = plantDesignVerifier?.uploadedDocument.map((doc) => ({
      text: 'Uploaded',
      value: doc.name,
    }));

    subSections.push({
      title: 'Designer and design verifier statement',
      details: statementSummary,
    });
  }

  return subSections;
};

const mapDesignerOrVerifierSummary = (details) => {
  const LABELS = PlantDesignDesignerVerifierPage.personDetails;

  return [
    { text: LABELS.firstNameLabel, value: details.firstName },
    { text: LABELS.middleNameLabel, value: details.middleName },
    { text: LABELS.lastNameLabel, value: details.lastName },
    { text: LABELS.businessLabel, value: details.businessName },
  ];
};

export {
  formatPlantDesignSummary,
  mapApplicantSummary,
  mapPlantDesignHolderSummary,
  mapPlantDesignSummary,
  mapDesignerOrVerifierSummary,
};
